import type { ProductSearchResult } from '@kimuno/api-sdk/lib/types/responses/Product';
import { Box } from '@mui/material';
import React, { useContext } from 'react';
import { Tab } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroller';
import { useSelector } from 'react-redux';

import {
	getPaginationSelector,
	getProductLoadingState as getProductLoadingStateSelector,
	getSearchResults as getSearchResultsSelector,
	SearchLoadingStatus,
} from '../../../../reducers/search.selectors';
import { trackingEvents } from '../../../../services/tracking/trackConfig';
import { MasterLayoutContext } from '../../../../state/MasterLayoutProvider';
import { SearchResultsTab } from '../../../../types/search';
import { hasNext } from '../../../../utils/pagination';
import useSearchExperiments from '../../../../utils/useSearchExperiments';
import ProductMuiCard, {
	type ProductMuiCardProps,
} from '../../../cards/MuiCard/ProductMuiCard/ProductMuiCard';
import WithTracking from '../../../utils/WithTracking';
import RelatedTopicsPills, {
	RELATED_TOPICS_INLINE_INDEX,
} from '../../RelatedTopicsPills/RelatedTopicsPills';
import EmptySearchResultsNewUI from '../EmptySearchResultsNewUI';
import { getCommonCardProps } from '../SearchResults';
import SearchResultsContainer from '../SearchResultsContainer';
import SearchResultsLoader from '../SearchResultsLoader';
import SearchResultsTopbarNewUI from '../SearchResultsTopbar';
import SingleSearchResultContainer from '../SingleSearchResultContainer';
import type { SearchResultsTabProps } from './SearchResultsTab';

export const getProductMuiCardProps = (
	item: ProductSearchResult,
	useAiFilters?: boolean
): ProductMuiCardProps => {
	return {
		...getCommonCardProps(item, useAiFilters),
		numberOfAttachments: item.attachmentsCount,
		numberOfMindItems: item.mindItemsCount,
		mindItemPageNumber: item.bestMatch?.mindItemPageNumber,
		bestMatchMindItemId: item.bestMatch?.mongoItemId,
	};
};

const ProductsTab = ({
	searchTerm,
	requestFilters,
	loadMore,
	searchEngineVersion,
	onShowNotification,
	onRelatedTopicSelect,
}: SearchResultsTabProps) => {
	const products = useSelector((state) => getSearchResultsSelector(state, 'products'));
	const productsPagination = useSelector(getPaginationSelector('products'));
	const productLoadingState = useSelector(getProductLoadingStateSelector);
	const { mainContentContainer } = useContext(MasterLayoutContext);
	const { useAiFilters } = useSearchExperiments();

	return (
		<Tab.Pane eventKey={SearchResultsTab.PRODUCTS} data-testid="new-ui-product-tab">
			<SearchResultsTopbarNewUI
				total={productsPagination.total}
				searchTerm={searchTerm}
				isLoaded={productLoadingState === SearchLoadingStatus.LOADED}
				requestFilters={requestFilters}
			/>
			{productLoadingState === SearchLoadingStatus.INITIAL ||
			productLoadingState === SearchLoadingStatus.NONE ? (
				<SearchResultsLoader />
			) : (
				<>
					{products.length > 0 ? (
						<InfiniteScroll
							loadMore={loadMore}
							hasMore={
								hasNext(productsPagination) && productLoadingState !== SearchLoadingStatus.PAGINATION
							}
							className="search-results__infinite-scroll-box"
							useWindow={false}
							getScrollParent={() => mainContentContainer}
						>
							<SearchResultsContainer>
								{products.map((product, index) => (
									<React.Fragment key={product.id}>
										<SingleSearchResultContainer>
											<WithTracking
												events={{
													onClick: trackingEvents.itemOpen,
													onMiddleClick: trackingEvents.itemOpen,
													onContextMenu: trackingEvents.itemOpen,
												}}
												eventPayload={{
													other: {
														product_id: product.id,
														index,
														searchEngineVersion,
													},
												}}
											>
												<Box>
													<ProductMuiCard
														{...getProductMuiCardProps(product, useAiFilters)}
														index={index}
														onShowNotification={onShowNotification}
													/>
												</Box>
											</WithTracking>
										</SingleSearchResultContainer>
										{index === RELATED_TOPICS_INLINE_INDEX && onRelatedTopicSelect && (
											<RelatedTopicsPills variant="inline" term={searchTerm} onSelect={onRelatedTopicSelect} />
										)}
									</React.Fragment>
								))}
							</SearchResultsContainer>
						</InfiniteScroll>
					) : (
						<SearchResultsContainer>
							<EmptySearchResultsNewUI searchTerm={searchTerm} />
						</SearchResultsContainer>
					)}
					{productLoadingState === SearchLoadingStatus.PAGINATION && <SearchResultsLoader />}
				</>
			)}
		</Tab.Pane>
	);
};

export default ProductsTab;
